import moment from "moment"
import ActionTypes, * as actionDicts from "shared/state_management/action-types"
import { DEFAULT_CUSTOM_DESCRIPTION_TEXT } from "shared/imports/sharedConstants"
import * as djangioActionCreators from "shared/djangio/action-creators"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

/**
* This function loads a data description object, the filter would be something like {person: id}
* where the key is the key for a QuorumDataType
* it might not exist, in which case we simply want to send an object
* with an empty string keyed on 'text'
* @name loadDataDescription
* @function
* @param {Object} qdt - QuorumDataType object of the current Profile, used to build Redux slice name and API request
* @param {Number} id - The ID of the current Profile
*/
export const loadDataDescription = ({
    qdt,
    id
}) => {
    return (dispatch, getState) => {
        return dispatch(djangioActionCreators.resourceGetList(
            DjangIO.app.userdata.models.DataDescription.objects
                .filter({ full_dehydrate_last_text_updated_user: true }),
            actionDicts.getDataDescriptionDict,
            { [qdt.related_field]: id },
            {
                qdtKey: qdt.key,
                id
            }
        ))
        .then(response => {
            response.objects.length
                ? dispatch({
                    type: ActionTypes.LOADED_DESCRIPTION,
                    dataDescription: response.objects[0],
                    qdtKey: qdt.key,
                    id,
                })
                : dispatch({
                    type: ActionTypes.LOADED_DESCRIPTION,
                    dataDescription: {},
                    qdtKey: qdt.key,
                    id,
                })
        })
        .catch(error => Promise.reject(error))
    }
}

/**
* This function updates the description in the redux store
* @name updateDataDescription
* @function
* @param {Object} qdt - QuorumDataType object for the current Profile, used to build the Redux slice name
* @param {Number} id - ID of the current Profile
* @param {String} newText - The new text to be stored in redux
*/
export const updateDataDescription = ({
    qdt,
    id,
    newText
}) => ({
    type: ActionTypes.UPDATE_DESCRIPTION,
    newText,
    qdtKey: qdt.key,
    id,
})

/**
* This function sends the DataDescription in the store to the backend
* Depending on what we find in the store, we either need to patch a currently existing DataDescription or create a new one
* @name submitDescriptionFromStore
* @function
* @param {Object} qdt - QuorumDataType object for the current Porfile, used to build the Redux slice name
* @param {Number} id - ID of the current Profile
* @param {Boolean} fromMobile - Whether this request is coming from the mobile application
*/
export const submitDescriptionFromStore = ({
    qdt,
    id,
    fromMobile = false
}) => {
    return (dispatch, getState) => {
        // get the description - we should always find it but lets be safe and check
        const immutableDescription = getState().customDescription.get(`${qdt.key}-${id}`)
        if (!immutableDescription) {
            return
        }
        // ok we safe now
        const description = immutableDescription.toJS()
        const text = fromMobile ? description.newText : description.text
        // if we have an id, we are patching, otherwise creating

        if (description.id) {
            // update the current description with the new text we have saved
            // in it
            return dispatch(djangioActionCreators.resourcePatchDetail(
                DjangIO.app.userdata.models.DataDescription.objects
                    .filter({ full_dehydrate_last_text_updated_user: true }),
                actionDicts.patchDataDescriptionDict,
                description.id,
                {
                    text,
                    last_text_updated_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                    text_last_updated: moment(),
                },
                {
                    qdtKey: qdt.key,
                    id
                },
            ))
            .then(response => {
                sendUserActionPost({
                    userActionType: DjangIO.app.useractions.types.UserActionType.custom_description_edited,
                    description: `${qdt.singular} Custom Description Edited`,
                    quorumDataType: qdt.value,
                })

                return dispatch({
                    type: ActionTypes.LOADED_DESCRIPTION,
                    dataDescription: response,
                    qdtKey: qdt.key,
                    id,
                })
            })
            .catch(error => Promise.reject(error))
        } else {
            // create the description with the text we have saved for it
            // and passing the necessary parameters
            const createObj = {
                data_type: qdt.value,
                [qdt.related_field]: DjangIO.newResourceFromDataType(qdt.value).foreignKey(id),
                organization: DjangIO.app.userdata.models.Organization.foreignKey(Userdata.organization_id),
                text,
                last_text_updated_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                text_last_updated: moment(),
            }

            return dispatch(djangioActionCreators.resourcePostList(
                DjangIO.app.userdata.models.DataDescription.objects
                    .filter({ full_dehydrate_last_text_updated_user: true }),
                actionDicts.postDataDescriptionDict,
                createObj,
                {
                    qdtKey: qdt.key,
                    id
                },
            ))
            .then(response => {
                sendUserActionPost({
                    userActionType: DjangIO.app.useractions.types.UserActionType.custom_description_created,
                    description: `${qdt.singular} Custom Description Created`,
                    quorumDataType: qdt.value,
                })

                return dispatch({
                    type: ActionTypes.LOADED_DESCRIPTION,
                    dataDescription: response,
                    qdtKey: qdt.key,
                    id,
                })
            })
            .catch(error => Promise.reject(error))
        }
    }
}

export const beginEditing = ({
    qdt,
    id
}) => ({
    type: ActionTypes.EDIT_DESCRIPTION,
    qdtKey: qdt.key,
    id
})

export const cancelEditing = ({
    qdt,
    id
}) => ({
    type: ActionTypes.CANCEL_EDIT_DESCRIPTION,
    qdtKey: qdt.key,
    id,
})

export const updateDescriptionMobile = ({
    qdt,
    id,
    newText
}) => ({
    type: ActionTypes.UPDATE_DESCRIPTION_MOBILE,
    qdtKey: qdt.key,
    id,
    newText
})

export const setCurrentTab = ({ qdt, id, newTab }) => ({
    type: ActionTypes.SET_CURRENT_TAB,
    qdt,
    id,
    newTab
})

export const setTitleIsTruncated = ({ qdt, id, isTruncated=false }) => ({
    type: ActionTypes.SET_TITLE_IS_TRUNCATED,
    qdt,
    id,
    isTruncated,
})
